/* eslint-disable react/prop-types,no-plusplus */
import React, { CSSProperties, Component } from 'react';
import styles from './scale-slide.css';
import { IReactionDisposer, reaction } from 'mobx';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import classNames from 'classnames';
import { RenderLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/thumbnail';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import isEqual from 'lodash/isEqual';
import { disposeOnUnmount } from 'mobx-react';
import AsyncBoundary from '@src/framework/frame/components/async-boundary';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';

interface ScaleSlideProps {
    isSelected?: boolean;
    slideModel: ISlideModel;
    region: string;
    buildRenderTreeAsync?: boolean;
    styles: { scaleSlideStyle?: CSSProperties, containerStyle?: CSSProperties };
    // 右侧边栏的单例
    isSingletonFromDrawer?: boolean;
    // 注入的服务
    selectionService?: ISelectionService;
    uiDataService?: IUiDataService;
    scale?: number;
    editMode?: string;
    width: number;
    height: number;
    isTemplate?: boolean;
    setCanvasContainerRef?: (ref: HTMLElement|null) => void;
    onDidRenderCallback?: (...arg: any[]) => any;
    isShown?: boolean;
    shouldUpdate?: boolean;
    fromScale?: number;
    previewSlideWidth?: number;
    /**
     * 是否回收app内存，并保存canvas图像，例如列表里的缩略图，在渲染完后，离开了屏幕，这时便需要回收内存保留图像
     */
    needDisposeAppAndKeepCanvas?: boolean;
}

interface ScaleSlideState {
    shouldUpdate: boolean;
    scale: number;
}

class ScaleSlide extends Component<ScaleSlideProps, ScaleSlideState> {
    public static defaultProps = {
        styles: {},
    };

    private isComponentMounted = false;
    private modelAutorun: IReactionDisposer;
    private curChangeReaction: IReactionDisposer;
    private renderLayer: RenderLayerService|null;
    private scale: number;
    private canvasRef: HTMLCanvasElement|null;

    public constructor(props, context) {
        super(props, context);

        this.state = {
            scale: this.props.scale || 1,
            shouldUpdate: false,
        };
    }

    public componentDidMount() {
        this.isComponentMounted = true;
        const { slideModel } = this.props;
        this.modelAutorun = reaction(() => ({
            displayChildrenElement: slideModel?.displayChildrenElement,
        }), this.updateComponent);

        slideModel?.changeSetEngine.addChangesetObserver(this.updateComponent);

        disposeOnUnmount(this, reaction(() => ({
            childrenConverted: slideModel?.childrenConverted,
        }), this.updateComponent));

        // 抽屉里的scale-slide是个单例，无法在内部监听slideModel的变化来更新组件，所以在这里动态销毁
        if (this.props.isSingletonFromDrawer) {
            this.curChangeReaction = reaction(
                () => this.getSelectionService()?.currentSlide,
                () => {
                    this.renderLayer?.destroyLayer?.();
                    this.renderLayer = null;
                },
            );
        }

        this.initRenderLayerIfNeed();
    }

    public updateComponent = () => {
        if (this.isComponentMounted) {
            this.setState({
                shouldUpdate: true,
            });
        }
    };

    public componentWillUnmount() {
        this.isComponentMounted = false;
        if (this.modelAutorun) {
            this.modelAutorun();
        }
        this.curChangeReaction?.();

        const { slideModel } = this.props;
        slideModel?.changeSetEngine.removeChangesetObserver(this.updateComponent);
        this.renderLayer?.destroyLayer?.();
        this.renderLayer = null;
    }

    public shouldComponentUpdate(nextProps, nextState) {
        // console.log('~_~ scaleSlideShouldUpdate ', nextProps, nextState);
        let shouldUpdate = false;
        if (nextState.shouldUpdate) {
            shouldUpdate = true;
        } else if (nextProps.shouldUpdate
            || nextProps.editMode !== this.props.editMode
            || nextProps.slideModel !== this.props.slideModel
            || nextProps.height !== this.props.height
            || nextProps.needDisposeAppAndKeepCanvas !== this.props.needDisposeAppAndKeepCanvas
            || !isEqual(nextProps.styles, this.props.styles)
        ) {
            shouldUpdate = true;
        }
        return shouldUpdate;
    }

    public componentDidUpdate() {
        if (this.state.shouldUpdate) {
            this.setState({
                shouldUpdate: false,
            });
        }
        this.initRenderLayerIfNeed();
    }

    public render() {
        const currentSlide = this.props.slideModel;
        const { slideWidth, slideHeight } = this.getUiDataService().presentation.props;
        if (!currentSlide || slideWidth === 0 || slideHeight === 0) return null;

        this.scale = this.getScale(slideWidth, slideHeight);

        const { isTemplate } = this.props;
        const scaleHeight = slideHeight * this.scale;
        const heightStyle = isTemplate ? { height: scaleHeight, position: 'absolute' } : { position: 'absolute' };
        return (
            <div
                className={classNames({
                    ScaleSlide: true,
                    [styles.templateCanvas]: isTemplate,
                })}
                style={{
                    ...(heightStyle as CSSProperties),
                    ...this.props.styles.scaleSlideStyle,
                }}
                id={currentSlide.domId(this.props.region)}
                onTouchStart={this.handleTouchStartOnCanvas}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: slideWidth * this.scale,
                        height: slideHeight * this.scale,
                        top: 0,
                        left: 0,
                        overflow: 'hidden',
                        outline: isTemplate ? '1.5px solid rgba(0,0,0,0.08)' : '',
                        ...this.props.styles.containerStyle,
                    }}
                    ref={this.props.setCanvasContainerRef}
                >
                    <canvas
                        width={slideWidth * this.scale}
                        height={slideHeight * this.scale}
                        ref={this.setCanvasRef}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transformOrigin: 'top left',
                            transform: `scale(${this.scale})`,
                            width: slideWidth,
                            height: slideHeight,
                            overflow: 'hidden',
                        }}>
                        <AsyncBoundary>
                            {async () => this.getViewableServiceLayer().watermarkManager.promise()
                                .then(watermarkManager => watermarkManager.getLazyWatermarkMask())}
                        </AsyncBoundary>
                    </div>
                </div>
                {this.showPreviewMask() ? <div
                    style={{
                        position: 'absolute',
                        transformOrigin: 'top left',
                        transform: `scale(${this.scale})`,
                        width: slideWidth, // Hardcoded to 1100:850 aspect ratio
                        height: slideHeight,
                        top: 0,
                        left: 0,
                    }}
                >
                </div> : null}
            </div>
        );
    }

    private getPageScale = () => this.scale;

    private handleTouchStartOnCanvas = () => {
        // 这里只处理移动端浏览态的插件点击隐藏事件
        this.getViewableServiceLayer().addonManager.updateShapeModel();
    };

    /**
     * 销毁app，但是保留canvas
     * @private
     */
    private destroyAppAndKeepCanvasView() {
        if (this.renderLayer) {
            this.renderLayer.destroyLayer(false);
            this.renderLayer = null;
        }
    }

    /**
     * 创建/更新缩略图app
     * @private
     */
    private buildAndUpdateComponent() {
        if (this.canvasRef) {
            if (!this.renderLayer) {
                this.renderLayer = new RenderLayerService(
                    this.props.slideModel,
                    this.canvasRef,
                    this.scale,
                    this.getPageScale,
                    this.props.region,
                    false,
                    this.props.buildRenderTreeAsync,
                    AppServiceProvider.getInstance().getAppServices()
                        .getInfrastructureService(),
                    this.getUiDataService(),
                    this.getMediaManager(),
                    this.props.onDidRenderCallback,
                    this.getSelectionService(),
                    this.getViewableServiceLayer(),
                    this.getEditableServiceLayer(),
                );
            } else {
                this.renderLayer.setScale(this.scale);
            }
        }
    }

    private initRenderLayerIfNeed() {
        if (this.scale > 0 && this.canvasRef && this.props.slideModel.childrenConverted) {
            // 视图外，销毁app并保留canvas图形
            if (this.props.needDisposeAppAndKeepCanvas) {
                this.destroyAppAndKeepCanvasView();
            } else {
                // 视图内，构建或者更新app
                this.buildAndUpdateComponent();
            }
        }
    }

    private setCanvasRef = (element) => {
        this.canvasRef = element;
    };

    private showPreviewMask() {
        return !browser.isMobile || this.props.editMode;
    }

    private getScale(slideWidth, slideHeight) {
        return this.props.width / slideWidth < this.props.height / slideHeight
            ? this.props.width / slideWidth : this.props.height / slideHeight;
    }

    private getViewableServiceLayer() {
        return this.getAppServices().viewableServiceLayer;
    }

    private getEditableServiceLayer() {
        return this.getAppServices().editableServiceLayer;
    }

    private getSelectionService() {
        if (this.props.selectionService) return this.props.selectionService;
        return this.getAppServices().viewableServiceLayer?.selectionService;
    }

    private getMediaManager() {
        return this.getAppServices().viewableServiceLayer?.mediaManager;
    }

    private getUiDataService() {
        if (this.props.uiDataService) return this.props.uiDataService;
        return this.getAppServices().getUiDataService();
    }

    private getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }
}

export default ScaleSlide;
